/**
 * Frame Animation Service
 * Handles frame sequence animation with configurable parameters
 */

import { AnimationModeConfig, EasingFunction } from '../config/animationConfig'

export interface AnimationFrameCallback {
  onFrameShow: (frameIndex: number) => void
  onComplete: (targetFrameIndex: number) => void
  onEffectsUpdate?: (progress: number, frameIndex: number, totalFrames: number) => void  // Optional: for effects animations
}

export class FrameAnimationService {
  private animationTimeoutRef: NodeJS.Timeout | null = null
  private isAnimatingRef: boolean = false

  /**
   * Apply easing function to progress value (0-1)
   */
  private applyEasing(progress: number, easingFn?: EasingFunction): number {
    if (easingFn === 'ease-out') {
      return 1 - Math.pow(1 - progress, 3)
    } else if (easingFn === 'ease-in') {
      return Math.pow(progress, 3)
    } else if (easingFn === 'ease-in-out') {
      return progress < 0.5 ? 4 * progress * progress * progress : 1 - Math.pow(-2 * progress + 2, 3) / 2
    }
    return progress // linear
  }

  /**
   * Animate through a sequence of frames with configurable parameters
   * @param frames - Array of frame indices to animate through
   * @param targetFrame - Final frame to settle on
   * @param config - Animation configuration (duration, sampling rate, etc.)
   * @param callbacks - Callbacks for frame changes and completion
   * @returns Cancel function to stop animation
   */
  public animateFrameSequence(
    frames: number[],
    targetFrame: number,
    config: AnimationModeConfig,
    callbacks: AnimationFrameCallback,
  ): () => void {
    // Cancel any existing animation
    this.cancel()

    this.isAnimatingRef = true

    const framesToShow = this.sampleFrames(frames, config.frameSamplingRate)

    console.log(
      `🎬 [${config.label}] Animation: ${framesToShow.length} frames @ ${config.frameDuration}ms/frame`,
    )

    let frameIndex = 0
    let pauseWaitCount = 0

    const showNextFrame = () => {
      if (!this.isAnimatingRef) {
        console.log(`🎬 [${config.label}] Animation cancelled`)
        return
      }

      if (frameIndex < framesToShow.length) {
        const currentFrame = framesToShow[frameIndex]
        const position = `${frameIndex + 1}/${framesToShow.length}`

        if (frameIndex === 0 || frameIndex === framesToShow.length - 1) {
          console.log(`🎬 [${config.label}] Frame ${position}: ${currentFrame}`)
        }

        callbacks.onFrameShow(currentFrame)

        // Calculate and apply effects if configured
        if (callbacks.onEffectsUpdate && config.effects) {
          const progress = frameIndex / (framesToShow.length - 1)
          const easedProgress = this.applyEasing(progress, config.easingFunction)
          callbacks.onEffectsUpdate(easedProgress, frameIndex, framesToShow.length)
        }

        frameIndex++
        this.animationTimeoutRef = setTimeout(showNextFrame, config.frameDuration)
      } else {
        // Animation complete
        console.log(`✅ [${config.label}] Complete (${framesToShow.length}/${framesToShow.length}) → frame ${targetFrame}`)

        this.isAnimatingRef = false
        this.animationTimeoutRef = null
        callbacks.onComplete(targetFrame)
      }
    }

    showNextFrame()

    // Return cancel function
    return () => this.cancel()
  }

  /**
   * Sample frames based on sampling rate
   * @param frames - Array of frame indices
   * @param samplingRate - Sample every nth frame (1 = all, 2 = every 2nd, etc.)
   * @returns Sampled frame array
   */
  private sampleFrames(frames: number[], samplingRate: number): number[] {
    if (samplingRate <= 1) return frames

    const sampled: number[] = []
    for (let i = 0; i < frames.length; i += samplingRate) {
      sampled.push(frames[i])
    }

    // Always include the last frame if not already included
    const lastFrame = frames[frames.length - 1]
    if (sampled[sampled.length - 1] !== lastFrame) {
      sampled.push(lastFrame)
    }

    return sampled
  }

  /**
   * Check if animation is currently running
   */
  public isAnimating(): boolean {
    return this.isAnimatingRef
  }

  /**
   * Cancel current animation
   */
  public cancel(): void {
    if (this.animationTimeoutRef) {
      clearTimeout(this.animationTimeoutRef)
      this.animationTimeoutRef = null
    }
    this.isAnimatingRef = false
  }

  /**
   * Cleanup resources
   */
  public destroy(): void {
    this.cancel()
  }
}

/**
 * Create a singleton instance of FrameAnimationService
 */
let animationServiceInstance: FrameAnimationService | null = null

export function getFrameAnimationService(): FrameAnimationService {
  if (!animationServiceInstance) {
    animationServiceInstance = new FrameAnimationService()
  }
  return animationServiceInstance
}

/**
 * Create SEPARATE singleton instances for pan-move and pan-end animations
 * This prevents one animation type from cancelling the other
 */
let panMoveAnimationService: FrameAnimationService | null = null
let panEndAnimationService: FrameAnimationService | null = null
let doubleClickAnimationService: FrameAnimationService | null = null
let shopCardPreviewAnimationService: FrameAnimationService | null = null

export function getPanMoveAnimationService(): FrameAnimationService {
  if (!panMoveAnimationService) {
    panMoveAnimationService = new FrameAnimationService()
  }
  return panMoveAnimationService
}

export function getPanEndAnimationService(): FrameAnimationService {
  if (!panEndAnimationService) {
    panEndAnimationService = new FrameAnimationService()
  }
  return panEndAnimationService
}

/**
 * Get the double-click animation service instance
 * Separate from pan animations to avoid interference
 */
export function getDoubleClickAnimationService(): FrameAnimationService {
  if (!doubleClickAnimationService) {
    doubleClickAnimationService = new FrameAnimationService()
  }
  return doubleClickAnimationService
}

/**
 * Get the shop card preview animation service instance
 * Separate from double-click animation to prevent interference
 */
export function getShopCardPreviewAnimationService(): FrameAnimationService {
  if (!shopCardPreviewAnimationService) {
    shopCardPreviewAnimationService = new FrameAnimationService()
  }
  return shopCardPreviewAnimationService
}
